import { Component, OnInit, Input, Output, EventEmitter, Injectable } from '@angular/core';
import { FormGroup, Validators, AbstractControl, FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { getCurrencyMask } from '../../../../configs/input.config';
import { AppConfigService } from '../../../app-config.service';

@Injectable()
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}

@Component({
  selector: 'app-currency-input',
  templateUrl: './currency-input.component.html',
  styleUrls: ['./currency-input.component.scss'],
  standalone: false
})
export class CurrencyInputComponent implements OnInit {
  @Input()
  id: string;

  @Input()
  customError?: string;

  @Input()
  customErrorText: string | object;

  @Input()
  doesEmitting: boolean;

  @Input()
  parentFormGroup: FormGroup;

  @Input()
  formName: string;

  @Input()
  textAlign = 'right';

  @Input()
  required = false;

  @Input()
  label: string;

  @Input()
  frequence: string;

  @Input()
  minInput: number;

  @Input()
  customClasses?: string[] = [];

  @Input()
  set maxInput(maxInput: number) {
    setTimeout(() => {
      this.maxInputValue = maxInput;
      this.parentFormGroup.get(this.formName).updateValueAndValidity();
    });
  }

  @Input()
  maxInputErrorMessage: string;

  @Input()
  minInputErrorMessage: string;

  @Input()
  isRelativeErrorMessage = false;

  @Input()
  initialValue = null;

  @Input()
  accessibilityLabel: string;

  @Input()
  shouldDisplayDollarSign: boolean;

  @Output()
  valueChange: EventEmitter<number> = new EventEmitter();

  errorStateMatcher = new MyErrorStateMatcher();

  maxInputValue: number;

  mask: string;

  unMaskFunction: Function;

  langue: string;

  constructor(private readonly _appConfigService: AppConfigService) {}

  emit() {
    const control = this.parentFormGroup.get(this.formName);
    const value = control.value;
    control.setValue(value ? this.unMaskFunction(String(value)) : null);
    if (this.doesEmitting && !this.hasErrors) {
      if (control.dirty) {
        this.valueChange.emit(control.value);
      }
    }
  }

  get customClassesString() {
    return this.customClasses.reduce((result, clazz) => `${result} ${clazz}`, '');
  }

  get hasErrors() {
    return !!this.parentFormGroup.controls[this.formName].errors;
  }

  ngOnInit() {
    if (!this.parentFormGroup) {
      this.formName = 'input';
      this.parentFormGroup = new FormGroup({ input: new FormControl(this.initialValue) });
    }
    this.configureValidators();
    [this.mask, this.unMaskFunction] = getCurrencyMask(this._appConfigService.configData.contexteEnvironnement.langue);
    this.langue = this._appConfigService.configData.contexteEnvironnement.langue;
  }

  updateFormValue(value: string): void {
    const sanatizedValue: string = value && String(this.unMaskFunction(value));
    const number = Number(sanatizedValue);
    if (isNaN(number)) {
      return;
    }
    this.parentFormGroup.get(this.formName).setValue(number);
    this.parentFormGroup.updateValueAndValidity();
    this.emit();
  }

  private configureValidators() {
    let validators = [
      Validators.min(this.minInput),
      (control: AbstractControl) => Validators.max(this.maxInputValue)(control)
    ];
    if (this.required) {
      validators = [...validators, Validators.required];
    }

    if (this.parentFormGroup.get(this.formName)) {
      this.parentFormGroup.get(this.formName).setValidators(validators);
    } else {
      throw new Error(
        `Form control non initialisé. parentFormGroup=${this.parentFormGroup}, formName=${
          this.formName
        }, this.parentFormGroup.get(this.formName)=${this.parentFormGroup.get(this.formName)}`
      );
    }
  }

  get customErrorMessage() {
    if (typeof this.customErrorText === 'object' && this.customError.length) {
      return this.customErrorText[this.customError];
    }

    return this.customErrorText;
  }
}
